import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { buildCommentTags } from 'features/libra/comments/commentsApi'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useRolodexEntries } from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import { useCallback, useMemo } from 'react'
import { RolodexEntryType } from 'types'

import type { SignoffsField } from './signoffTypes'
import { getSignoffEntryId, parseSignoffEntry } from './signoffTypes'

const SWR_OPTIONS = {
  compare: isEqual,
  revalidateOnFocus: true,
  refreshInterval: 15_000,
} as const

/** The ticket's sign-off state, read from the entry tagged to it. */
export function useTicketSignoffEntry(ticketId: string) {
  const queryBody = useMemo(
    () => ({
      type: RolodexEntryType.LIBRA_TICKET_SIGNOFF,
      tags: { [RolodexEntryType.LIBRA_TICKET]: ticketId },
    }),
    [ticketId]
  )

  const { entries, isLoading, forceRefresh } = useRolodexEntries(queryBody, {
    swrOptions: SWR_OPTIONS,
  })

  const signoffs = useMemo(
    () => parseSignoffEntry(entries?.[0]?.data),
    [entries]
  )

  return useMemo(
    () => ({ signoffs, isLoading, forceRefresh }),
    [signoffs, isLoading, forceRefresh]
  )
}

/**
 * Sign-off state for many tickets in one request, keyed by ticket id. Entry
 * ids are derived from ticket ids, so this is a plain id lookup.
 */
export function useTicketsSignoffEntries(ticketIds: ReadonlyArray<string>) {
  const queryBody = useMemo(
    () =>
      ticketIds.length > 0
        ? {
            type: RolodexEntryType.LIBRA_TICKET_SIGNOFF,
            ids: ticketIds.map(getSignoffEntryId),
          }
        : undefined,
    [ticketIds]
  )

  const { entries, isLoading } = useRolodexEntries(queryBody)

  const signoffsByTicketId = useMemo(
    () =>
      new Map(
        (entries ?? []).map((entry) => [
          entry.tags[RolodexEntryType.LIBRA_TICKET] as string,
          parseSignoffEntry(entry.data),
        ])
      ),
    [entries]
  )

  return useMemo(
    () => ({ signoffsByTicketId, isLoading }),
    [signoffsByTicketId, isLoading]
  )
}

/**
 * Writes `next` as a sparse patch against `current`. Always a PATCH carrying
 * type and tags, so the first write creates the entry.
 */
export function useWriteSignoffs() {
  const { rolodexPatch } = useRolodexEdit()

  return useCallback(
    async (
      ticket: LibraTicket,
      current: SignoffsField,
      next: SignoffsField
    ) => {
      const patch = createPatchData(
        getSignoffEntryId(ticket.id),
        { signoffs: current },
        { signoffs: next }
      )
      if (!patch) return

      await rolodexPatch([
        {
          ...patch,
          type: RolodexEntryType.LIBRA_TICKET_SIGNOFF,
          tags: buildCommentTags(ticket, ticket.workflowId),
        },
      ])
    },
    [rolodexPatch]
  )
}
